import { jsx as _jsx } from "react/jsx-runtime";
import { useIsPHIconReady } from "@icon/phosphor/useIsPHIconReady";
import { SkeletonLoader } from "@widget/loaders";
import React from "react";
import { parsePhosphorIcon, getSizeValue, getAnimationClasses, getTransformStyle } from "../utils";
/**
 * @export
 *
 * Wrapper for Phosphor Icons web components with FontAwesome-compatible API
 * This shows a loading state (skeleton loader) until the icon web component is ready,
 * then renders the actual icon.
 *
 * **Parent app must load Phosphor Icons web components via CDN:**
 * ```html
 * <script type="module" src="https://unpkg.com/@phosphor-icons/webcomponents@2.1"></script>
 * ```
 *
 * @example
 * ```tsx
 * // Simple usage with default regular weight
 * <PHIconWrapper icon="house" />
 * ```
 *
 * @example
 * ```tsx
 * // With weight prefix and FontAwesome-style size
 * <PHIconWrapper icon={['phb', 'house']} size="2x" color="blue" />
 * ```
 *
 * @example
 * ```tsx
 * // With animations
 * <PHIconWrapper icon={['phf', 'heart']} spin color="red" size="lg" />
 * ```
 *
 * @example
 * ```tsx
 * // With flip and rotation
 * <PHIconWrapper icon="arrow-right" flip="horizontal" rotation={90} />
 * ```
 */
export const PHIconWrapper = (props) => {
    const { icon, size, color, className, style, flip, rotation, skeletonLoader } = props;
    const { weight, iconName } = parsePhosphorIcon(icon);
    const ready = useIsPHIconReady(iconName);
    if (!ready) {
        const defaultSkeletonProps = {
            width: "100%",
            height: "100%",
            additionalStyle: {
                borderRadius: "50%",
                aspectRatio: "1",
            }
        };
        const mergedSkeletonProps = skeletonLoader
            ? Object.assign(Object.assign(Object.assign({}, defaultSkeletonProps), skeletonLoader), { additionalStyle: Object.assign(Object.assign({}, defaultSkeletonProps.additionalStyle), skeletonLoader.additionalStyle) }) : defaultSkeletonProps;
        return _jsx(SkeletonLoader, Object.assign({}, mergedSkeletonProps));
    }
    // Get animation classes
    const animationClasses = getAnimationClasses(props);
    // Get transform style
    const transformStyle = getTransformStyle(flip, rotation);
    // Combine class names
    const wrapperClassName = [
        'uxpcore-phicon-wrapper',
        ...animationClasses,
        className
    ].filter(Boolean).join(' ');
    // Convert size
    const sizeValue = getSizeValue(size);
    // Combine styles
    const wrapperStyle = Object.assign(Object.assign({}, style), (transformStyle && { transform: transformStyle }));
    // Custom element name format: ph-{iconName}
    const elementName = `ph-${iconName}`;
    // Render web component wrapped in span for animations and transforms
    return (_jsx("span", { className: wrapperClassName, style: wrapperStyle, children: React.createElement(elementName, {
            weight,
            size: sizeValue,
            color,
        }) }));
};
